import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import IosShareRounded from "@mui/icons-material/IosShareRounded";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useState, type MouseEvent, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

/**
 * The approved detail-screen bar: a back arrow, the screen's name with one
 * quiet context line under it, and up to two trailing icon actions. This is the
 * only heading on a detail screen (no separate big page title).
 */
export function DetailHeader({
  back,
  title,
  subtitle,
  actions,
  titleComponent = "h1",
  wrapSubtitle = false,
}: {
  /**
   * Where the arrow goes; `label` is its accessible name ("Back to facility").
   * `onClick` runs first and may `preventDefault()` to navigate itself.
   */
  back?: { to: string; label: string; onClick?: (event: MouseEvent<HTMLElement>) => void } | null;
  /** Omit when the screen's own header carries the h1 (a profile). */
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** "p" when the screen's h1 sits lower (the booking pass names the court). */
  titleComponent?: "h1" | "p";
  /** Let a sentence-length subtitle wrap instead of truncating. */
  wrapSubtitle?: boolean;
}) {
  return (
    <Box
      component="header"
      data-testid="detail-header"
      sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
    >
      {back ? (
        <IconButton
          component={RouterLink}
          to={back.to}
          aria-label={back.label}
          edge="start"
          onClick={back.onClick}
        >
          <ArrowBackRounded />
        </IconButton>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title ? (
          <Typography
            component={titleComponent}
            sx={{ fontSize: "1.125rem", fontWeight: 700, lineHeight: 1.25, overflowWrap: "anywhere" }}
          >
            {title}
          </Typography>
        ) : null}
        {subtitle ? (
          <Typography noWrap={!wrapSubtitle} variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" sx={{ flexShrink: 0, alignItems: "center" }}>
          {actions}
        </Stack>
      ) : null}
    </Box>
  );
}

/**
 * Share the current screen: the system share sheet where the browser has one,
 * otherwise the link is copied. Shares only the page URL and its title.
 */
export function ShareButton({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);
  async function share() {
    const url = window.location.href;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // A dismissed share sheet or a blocked clipboard is not an error to show.
    }
  }
  return (
    <Tooltip title={copied ? "Link copied" : "Share"}>
      <IconButton aria-label={copied ? "Link copied" : "Share"} onClick={() => void share()} edge="end">
        <IosShareRounded />
      </IconButton>
    </Tooltip>
  );
}
